import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { refreshSession, rememberAccessToken, forgetAccessToken } from '../services/api';
import { getAccessToken, clearLegacyTokenStorage, hasSessionHint, clearSessionHint } from '../utils/accessToken';
import * as subscriptionService from '../services/subscriptionService';
import { initializeClarity, clearClarityUser } from '../services/clarityService';
import { mayInitializeClarity } from '../services/analyticsConsent';
import { postAttributionFromStorage, trackProductEvent } from '../services/analyticsService';
import { clearAdminMode } from '../utils/host';
import Logger from '../utils/logger';

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getAccessToken());
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [trialEnd, setTrialEnd] = useState(null);
  const [isFoundingMember, setIsFoundingMember] = useState(false);
  const [subscriptionPortalUrl, setSubscriptionPortalUrl] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [skipNextValidation, setSkipNextValidation] = useState(false);
  const navigate = useNavigate();
  const hasTrackedAppOpen = useRef(false);

  // Stable refs to avoid effect loops from changing callbacks
  const subscriptionRef = useRef(null);
  const subscriptionLoadingRef = useRef(false);
  
  // Profile PATCH responses omit auth-only fields such as isAdmin and onboarding.
  const applyUserProfile = React.useCallback((profile) => {
    setUser(currentUser => ({ ...currentUser, ...profile }));
  }, []);

  // Track if timezone sync is in progress to prevent duplicate requests
  const timezoneSyncInProgress = useRef(false);
  
  /**
   * Detects browser timezone and syncs it to the user's profile if missing or set to UTC.
   * This ensures accurate date handling for transactions and budgets.
   * Only runs once per session to avoid redundant API calls.
   */
  const syncDeviceTimezone = React.useCallback(async (userData) => {
    // Skip if already syncing
    if (timezoneSyncInProgress.current) return;
    
    const currentTimezone = userData?.preferences?.locale?.timezone;
    
    // Only sync if timezone is missing or still set to default 'UTC'
    if (currentTimezone && currentTimezone !== 'UTC') {
      return;
    }
    
    try {
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Validate that we got a valid timezone
      if (!deviceTimezone || deviceTimezone === 'UTC') {
        return;
      }
      
      timezoneSyncInProgress.current = true;

      const existingPreferences = userData?.preferences || {};
      const existingLocale = existingPreferences.locale || {};
      const updatedPreferences = {
        ...existingPreferences,
        locale: {
          ...existingLocale,
          timezone: deviceTimezone
        }
      };

      // Update user preferences with detected timezone
      const response = await api.patch('/users/me', {
        preferences: updatedPreferences
      });
      
      // Update local user state with new timezone
      if (response.data?.user) {
        applyUserProfile(response.data.user);
      }
      
      Logger.debug('Synced browser timezone:', deviceTimezone);
    } catch (error) {
      // Non-critical error - log but don't disrupt auth flow
      Logger.warn('Failed to sync browser timezone:', error);
    } finally {
      timezoneSyncInProgress.current = false;
    }
  }, [applyUserProfile]);

  useEffect(() => {
    subscriptionRef.current = subscription;
  }, [subscription]);

  useEffect(() => {
    subscriptionLoadingRef.current = subscriptionLoading;
  }, [subscriptionLoading]);

  // Log auth state changes
  useEffect(() => {
    Logger.debug('Auth state changed:', {
      hasToken: !!token,
      hasUser: !!user,
      isAuthenticated: !!token && !!user,
      loading
    });
  }, [token, user, loading]);

  useEffect(() => {
    if (hasTrackedAppOpen.current) return;
    hasTrackedAppOpen.current = true;
    trackProductEvent('app_opened', {
      authState: token ? 'authenticated' : 'anonymous'
    });
  }, [token]);

  const hasFeature = (featureName) => {
    // Free tier features are always available
    const freeFeatures = ['basic_budgeting', 'transaction_tracking'];
    if (freeFeatures.includes(featureName)) {
      return true;
    }

    // Premium features require an active subscription or active trial
    const premiumFeatures = ['ai_insights', 'ai_categorization', 'advanced_analytics', 'unlimited_accounts'];
    if (!premiumFeatures.includes(featureName)) {
      return false;
    }
    const subStatus = subscription?.status;
    const isSubActive = subStatus === 'active' || subStatus === 'trialing';
    return isSubActive || isTrialActive === true;
  };

  const logout = React.useCallback(async () => {
    try {
      // Cookie supplies refresh for server revoke (TM-002); body omitted on web.
      await api.post('/auth/logout', {});
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      clearClarityUser();
      clearAdminMode();
      forgetAccessToken();
      clearLegacyTokenStorage();
      clearSessionHint();
      setToken(null);
      setUser(null);
      setSubscription(null);
      navigate('/', { replace: true });
    }
  }, [navigate]);

  /**
   * Validates the session token and loads the user.
   * @returns {Promise<object|null>} verified user on success, null on failure
   */
  const validateToken = React.useCallback(async (token) => {
    try {
      const response = await api.get('/auth/verify');
      const verifiedUser = response.data.user;
      setUser(verifiedUser);
      postAttributionFromStorage().catch(() => {});
      
      // Sync browser timezone if missing or set to UTC
      syncDeviceTimezone(verifiedUser);

      // #1216 / F1: Clarity is NOT auth-gated — login is not consent, and
      // signed-in financial screens must not be recorded until masking is
      // verified in the Clarity dashboard. mayInitializeClarity() requires
      // analytics consent AND blocks authenticated sessions by default.
      // Clarity is not given the account id. There is no per-user deletion
      // API, so sessions stay unidentified. #2592.
      if (mayInitializeClarity({ isAuthenticated: true })) {
        initializeClarity({ isAuthenticated: true });
      }

      return verifiedUser || null;
    } catch (error) {
      if (error.response?.status === 401) {
        // Access expired — rotate via HttpOnly cookie (empty body).
        try {
          const newToken = await refreshSession();
          if (!newToken) {
            await logout();
            return null;
          }
          setToken(newToken);
          const retry = await api.get('/auth/verify');
          const verifiedUser = retry.data.user;
          setUser(verifiedUser);
          return verifiedUser || null;
        } catch (refreshError) {
          await logout();
          return null;
        }
      }
      return null;
    }
  }, [logout, syncDeviceTimezone]);

  const loadSubscription = React.useCallback(async (force = false) => {
    // Skip if already loading or if not forced and subscription exists
    if (subscriptionLoadingRef.current || (!force && subscriptionRef.current)) {
      return subscriptionRef.current;
    }

    try {
      subscriptionLoadingRef.current = true;
      setSubscriptionLoading(true);
      const data = await subscriptionService.getCurrentSubscription();
      const newSubscription = data.subscription || null;
      setIsTrialActive(!!data.isTrialActive);
      setTrialEnd(data.trialEnd || null);
      setIsFoundingMember(!!data.isFoundingMember);
      setSubscriptionPortalUrl(data.portalUrl || null);
      setHasUsedTrial(!!data.hasUsedTrial);
      
      // Only update if subscription has changed
      if (JSON.stringify(newSubscription) !== JSON.stringify(subscriptionRef.current)) {
        setSubscription(newSubscription);
        subscriptionRef.current = newSubscription;
      }
      
      return newSubscription;
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.requireVerification) {
        // PIN verification required - let the error propagate
        throw error;
      }
      console.error('Error loading subscription:', error);
      // For other errors, set subscription to null but don't throw
      setSubscription(null);
      subscriptionRef.current = null;
      return null;
    } finally {
      subscriptionLoadingRef.current = false;
      setSubscriptionLoading(false);
    }
  }, []);

  // Listen for global subscription update events (e.g., post-checkout verification)
  useEffect(() => {
    const onSubUpdated = async () => {
      try {
        await loadSubscription(true);
      } catch (e) {}
    };
    window.addEventListener('subscription:updated', onSubUpdated);
    return () => window.removeEventListener('subscription:updated', onSubUpdated);
  }, [loadSubscription]);

  const fetchUserProfile = React.useCallback(async () => {
    try {
      const response = await api.get('/auth/verify');
      setUser(response.data.user);
      
      // Sync browser timezone if missing or set to UTC
      syncDeviceTimezone(response.data.user);
      
      if (response.data.user) {
        setIsFoundingMember(!!response.data.user.foundingMember);
      }
      await loadSubscription();
      return response.data.user;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }, [loadSubscription, syncDeviceTimezone]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        let sessionToken = token || getAccessToken();
        if (!sessionToken && !skipNextValidation && hasSessionHint()) {
          try {
            sessionToken = await refreshSession();
            if (sessionToken) {
              setToken(sessionToken);
            }
          } catch (_) {
            sessionToken = null;
            clearSessionHint();
          }
        }
        if (sessionToken && !skipNextValidation) {
          const isValid = await validateToken(sessionToken);
          if (isValid) {
            await loadSubscription();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
        setSkipNextValidation(false);
      }
    };

    initializeAuth();
  }, [token, skipNextValidation, loadSubscription, validateToken]);

  /**
   * Establish a session from a JWT.
   * @returns {Promise<object|null>} verified user (when validated) for post-auth routing
   */
  const login = async (token, email = null, skipValidation = false) => {
    rememberAccessToken(token);
    clearLegacyTokenStorage();
    setSkipNextValidation(skipValidation);
    setToken(token);

    if (!skipValidation) {
      const verifiedUser = await validateToken(token);
      if (!verifiedUser) {
        throw new Error('Failed to validate token');
      }
      await loadSubscription(true);
      trackProductEvent('user_login', {}).catch(() => {});
      // #1221: return user so Login can choose activity vs app handoff
      return verifiedUser;
    }
    return null;
  };

  // logout is defined above as a stable callback

  const updateUser = async (updates) => {
    try {
      const response = await api.patch('/users/me', updates);
      // Profile responses omit auth-derived fields such as isAdmin.
      applyUserProfile(response.data.user);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const value = {
    user,
    applyUserProfile,
    token,
    loading,
    subscription,
    subscriptionLoading,
    isTrialActive,
    trialEnd,
    isFoundingMember,
    subscriptionPortalUrl,
    hasUsedTrial,
    hasFeature,
    login,
    logout,
    updateUser,
    fetchUserProfile,
    validateToken,
    loadSubscription,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
